import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class OtpService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  private getAuthHeaders(): { headers: HttpHeaders } {
    const userStr = localStorage.getItem('loggedInUser');
    let headers = new HttpHeaders();
    if (userStr) {
      const user = JSON.parse(userStr);
      if (user && user.accessToken) {
        headers = headers.set('Authorization', `Bearer ${user.accessToken}`);
      }
    }
    return { headers };
  }

  sendOtp(mobile: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/otp/send/${mobile}`);
  }

  sendTroupeOtp(mobile: string, troupeCode: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/otp/sendTroupeUpdateOtp/${mobile}/${troupeCode}`, this.getAuthHeaders());
  }

  sendMemberOtp(mobile: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/otp/add-member/${mobile}`, this.getAuthHeaders());
  }

  verifyBankOtp(mobile: string, userType: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/otp/updateBankOtp/${mobile}/${userType}`, this.getAuthHeaders());
  }

  verifyOtp(phone: string, otp: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/otp/verify`, { phone, otp });
  }

  //Public URL for reseting password
  passwordResetOtp(mobile: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/otp/password/reset/${mobile}`);
  }

  passwordChangeOtp(mobile: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/otp/password/change/${mobile}`, this.getAuthHeaders());
  }

  successRegistration(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/otp/success-registration`, { body: data });
  }

  sendTroupeStatus(troupeId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/otp/send-troupe-status/${troupeId}`, this.getAuthHeaders());
  }
  sendAgencyStatus(raId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/otp/send-agency-status/${raId}`, this.getAuthHeaders());
  }

  sendCustomEmail(toEmail: string, subject: string, message: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/otp/send-email`, { toEmail, subject, message }, this.getAuthHeaders());
  }

  getActiveUsersCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.apiUrl}/otp/active-users-count`);
  }

  getApiUrl(): string {
    return this.apiUrl;
  }

  fetchCaptcha(): Observable<{ captchaId: string; imageUrl: string }> {
    return this.http.get(`${this.apiUrl}/captcha`, {
      responseType: 'blob',
      observe: 'response',
    }).pipe(
      switchMap((res) => {
        if (!res.body || res.body.size === 0) {
          throw new Error('Captcha image response was empty');
        }
        return from(this.blobToDataUrl(res.body)).pipe(
          map((imageUrl) => ({
            captchaId: res.headers.get('X-Captcha-Id') ?? '',
            imageUrl,
          })),
        );
      }),
    );
  }

  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error ?? new Error('Failed to read captcha image'));
      reader.readAsDataURL(blob);
    });
  }

  verifyCaptcha(value: string, captchaId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/captcha/validate`, {
      value: (value ?? '').trim(),
      captchaId,
    });
  }
}
